import { useEffect, useRef, useState } from "react";

/** The bar always takes at least this long, so opening a game feels "alive" (never a flash). */
const FLOOR_MS = 1600;
/** Let the bar visibly reach 100% before the table is revealed. */
const FILL_HOLD_MS = 260;

/** Test seam: component tests that assert TABLE behavior set `window.__BING_INSTANT_LOAD__`
 *  so the loading bar resolves the instant the first view arrives (no timed floor). Never
 *  set in production; the hook's OWN timing is covered by `use-solo-load.test.tsx`. */
function instantLoad(): boolean {
  return (
    typeof window !== "undefined" &&
    (window as { __BING_INSTANT_LOAD__?: boolean }).__BING_INSTANT_LOAD__ === true
  );
}

/**
 * Real-progress loading gate for a SOLO (offline) game. The bar creeps while the socket
 * connects, jumps when the FIRST authoritative view arrives, and only COMPLETES once BOTH
 * the view is in AND a short floor has elapsed — then it fires `onReady` (which tells the
 * server it may start the bots) and reveals the table. So the game never advances behind
 * the loading screen: bots stay frozen until `onReady`, and the opening ceremony then runs
 * exactly as before, just gated behind the finished bar.
 *
 * `resetKey` (the game store identity) restarts the bar on "play again"/a fresh connection.
 */
export function useSoloLoad(
  hasView: boolean,
  onReady: () => void,
  resetKey: unknown,
  /**
   * Extra precondition for REVEALING the table — the card-face chunk for the five card games
   * (`game/card-faces.ts`). Defaults true, so board games and every other caller are unchanged.
   *
   * ⚠️ THIS IS A FLOOR, NEVER A TIMEOUT. `FLOOR_MS` is the MINIMUM the bar takes; if the chunk
   * needs longer than that — and on the connections this app is built for it can — the bar simply
   * keeps waiting. It must never elapse and reveal a table whose cards cannot render. Because
   * `onReady` is also what tells the server to start the bots, gating here means the game cannot
   * advance behind the loading screen either.
   */
  assetsReady = true,
): { loading: boolean; progress: number; waitingForAssets: boolean } {
  const [progress, setProgress] = useState(0.08);
  const [done, setDone] = useState(false);
  const startRef = useRef(0);
  const readyRef = useRef(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const instant = instantLoad();

  // Reset the bar synchronously when the game (store) changes — the React-sanctioned
  // "adjust state on a changed input" pattern, so "play again" restarts the loader
  // without a stale-frame flash.
  const keyRef = useRef(resetKey);
  if (startRef.current === 0) startRef.current = Date.now();
  if (resetKey !== keyRef.current) {
    keyRef.current = resetKey;
    startRef.current = Date.now();
    readyRef.current = false;
    setProgress(0.08);
    setDone(false);
  }

  // Creep toward 0.6 while connecting; 0.92 once the first view has arrived.
  useEffect(() => {
    if (done || instant) return;
    const id = window.setInterval(() => {
      setProgress((p) => {
        const ceil = hasView ? 0.92 : 0.6;
        return p < ceil ? p + (ceil - p) * 0.14 : p;
      });
    }, 90);
    return () => window.clearInterval(id);
  }, [done, hasView, instant]);

  // Complete once the view is in, the assets are in AND the floor elapsed: snap to 100%, hold,
  // then reveal + signal ready. (Test bypass: resolve the moment the view lands.)
  useEffect(() => {
    if (done || !hasView || !assetsReady) return;
    const signalReady = (): void => {
      if (!readyRef.current) {
        readyRef.current = true;
        onReadyRef.current();
      }
    };
    if (instant) {
      signalReady();
      setDone(true);
      return;
    }
    const timers: number[] = [];
    const remaining = Math.max(0, FLOOR_MS - (Date.now() - startRef.current));
    timers.push(
      window.setTimeout(() => {
        setProgress(1);
        timers.push(
          window.setTimeout(() => {
            signalReady();
            setDone(true);
          }, FILL_HOLD_MS),
        );
      }, remaining),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [hasView, assetsReady, done, instant]);

  const waitingForAssets = hasView && !assetsReady;
  // The creep ceiling still keys on `hasView` alone, so a table whose view has landed but whose
  // artwork is still in flight sits at 0.92 rather than dropping back to the "connecting" 0.6 —
  // the bar tells the truth about which half is outstanding.
  if (instant)
    return { loading: !hasView || !assetsReady, progress: hasView ? 1 : 0.4, waitingForAssets };
  return { loading: !done, progress, waitingForAssets };
}
